import { CfFlowStatus, CfFlowStep } from '../api/cloudflareFlowApi';

/**
 * The steps the progress screen shows (PRE-3292 / F4).
 *
 * **This is not the list in the Figma frame, deliberately.** That frame shows
 * "Creating Cloudflare Worker / Deploying Worker code / Injecting
 * PRERENDER_TOKEN / Configuring domain route / Verifying integration", and it
 * does not match what the server actually does:
 *
 *  - **"Injecting PRERENDER_TOKEN" is not a step.** The token is bound in the
 *    same upload call as the Worker — confirmed against a live Cloudflare zone
 *    while building PRE-3285. Showing it separately would show a customer a
 *    step that can never fail on its own, and can never be the one that did.
 *  - **The DNS step is missing from the frame**, and it is the step that fails
 *    most often (`dns_proxy` in PRE-3288's failure enumeration). A progress list
 *    that hides the most common failure is worse than no list.
 * Verification is the one the frame was right about. It runs after the
 * deployment and renders on this same screen, and without a row a customer
 * whose deploy succeeded and whose verification failed reads four ticks and an
 * error card that blames the setup.
 *
 * Every other row is a key the server writes into `steps`, so a mark means a
 * step the server really recorded. {@link VERIFICATION_ROW_KEY} is the
 * exception and is read from the flow's status instead.
 */
/**
 * Not a server step key. The deployment's `steps` map never gains an entry for
 * verification, so this row reads the flow status and nothing writes it here.
 */
export const VERIFICATION_ROW_KEY = 'verification';

export const CF_SETUP_STEPS: ReadonlyArray<{
  /** The key the server writes into `steps`. */
  readonly key: string;
  readonly label: string;
}> = [
  { key: 'preflight', label: 'Checking your Cloudflare setup' },
  { key: 'ensure_proxied', label: 'Pointing your DNS records at Cloudflare' },
  { key: 'worker_upload', label: 'Deploying the Prerender Worker' },
  { key: 'route_attach', label: 'Configuring your domain route' },
  { key: VERIFICATION_ROW_KEY, label: 'Verifying integration' },
];

/**
 * The pre-flight is six server-side checks under one label: a customer does
 * not need six lines for work that either passes as a whole or stops the flow.
 *
 * Kept in step with `PREFLIGHT_STEP_KEYS` on the server. `dns_read` was added
 * there by PRE-3284 and missing here, so a zone whose DNS could not be read
 * left this row reading pending while the flow was already dead.
 */
const PREFLIGHT_KEYS = [
  'zone_active',
  'token_scope',
  'workers_enabled',
  'route_collision',
  'ssl_mode',
  'dns_read',
];

/**
 * Rows that stand for more than one server step.
 *
 * The account's workers.dev subdomain (`workers_subdomain`, moved into Phase 1
 * by PRE-3284) shares the Worker's row. It can fail on its own, so it has to be
 * visible somewhere — but a customer has no action for it that differs from the
 * upload, and the four rows above are the reviewed list. Without this it was on
 * no row at all: a subdomain failure showed four unfinished rows and no failure.
 *
 * A `Map` rather than an object literal because the key is a parameter.
 */
const AGGREGATE_ROWS = new Map<string, readonly string[]>([
  ['preflight', PREFLIGHT_KEYS],
  ['worker_upload', ['workers_subdomain', 'worker_upload']],
]);

export type StepDisplayState = 'pending' | 'running' | 'done' | 'failed';

type ServerStepState = CfFlowStep['state'];

const worstOf = (states: readonly ServerStepState[]): StepDisplayState => {
  // `rolled_back` counts as failed here for the same reason it does below: the
  // customer's account no longer carries the step's artefact.
  if (states.some((state) => state === 'failed' || state === 'rolled_back')) {
    return 'failed';
  }
  if (states.includes('running')) return 'running';
  if (states.length > 0 && states.every((state) => state === 'passed' || state === 'skipped')) {
    return 'done';
  }

  return 'pending';
};

/**
 * What one row of the progress list should show.
 *
 * A step the server has not recorded is `pending`, never `done` — an unrecorded
 * step is one that has not happened, and marking it complete would tell the
 * customer we changed something we did not.
 */
const VERIFICATION_STATE: Partial<Record<CfFlowStatus, StepDisplayState>> = {
  verifying: 'running',
  complete: 'done',
  verification_failed: 'failed',
};

export function stepDisplayState(
  key: string,
  steps: Record<string, CfFlowStep>,
  status?: CfFlowStatus,
): StepDisplayState {
  if (key === VERIFICATION_ROW_KEY) {
    return (status && VERIFICATION_STATE[status]) ?? 'pending';
  }

  const aggregated = AGGREGATE_ROWS.get(key);
  if (aggregated) {
    return worstOf(
      aggregated
        .map((serverKey) => steps[serverKey]?.state)
        .filter((state): state is ServerStepState => state !== undefined),
    );
  }

  const state = steps[key]?.state;
  if (state === undefined) return 'pending';
  if (state === 'failed') return 'failed';
  if (state === 'running') return 'running';
  if (state === 'passed' || state === 'skipped') return 'done';

  // `rolled_back` reads as failed: the customer's account no longer carries it.
  return 'failed';
}

/**
 * The row whose own server step failed. Unlike {@link stepDisplayState}, a
 * `rolled_back` step does not count: it is a step that worked and was undone
 * because a later one failed.
 */
export function failedRowKeyOf(steps: Record<string, CfFlowStep>): string | null {
  const failed = CF_SETUP_STEPS.find(({ key }) =>
    (AGGREGATE_ROWS.get(key) ?? [key]).some((serverKey) => steps[serverKey]?.state === 'failed'),
  );

  return failed?.key ?? null;
}

/**
 * The server records a step only once it has finished — it never writes
 * `running` — so the row being worked on is inferred: the first one not yet
 * done, while the flow is still in progress.
 */
const IN_PROGRESS_STATUSES: readonly CfFlowStatus[] = [
  'token_connected',
  'preflight_running',
  'deploying',
];

export function setupRowStates(
  steps: Record<string, CfFlowStep>,
  status?: CfFlowStatus,
): StepDisplayState[] {
  const states = CF_SETUP_STEPS.map(({ key }) => stepDisplayState(key, steps, status));
  const inProgress =
    status !== undefined &&
    IN_PROGRESS_STATUSES.includes(status) &&
    !states.includes('running') &&
    !states.includes('failed');
  const next = states.indexOf('pending');

  return inProgress ? states.map((state, index) => (index === next ? 'running' : state)) : states;
}
